import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/feature-slider.scss";

/**
 * What a StorkPlus store can do, one slide at a time. Sits in the hero of the
 * Google signup pages (store details form and the "building your store"
 * screen). Icons are plain line icons in the brand blue (no 3D emoji, no
 * motion); only the slide text changes. Auto-advances, pauses on hover/focus,
 * dots and arrows to browse by hand.
 */

const svg = (paths) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);

const ICONS = {
  ai: svg(<><path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 3v-3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z" /><path d="M8 10.5h.01M12 10.5h.01M16 10.5h.01" /></>),
  themes: svg(<><path d="M12 21a9 9 0 0 1 0-18c4.97 0 9 3.58 9 8 0 1.06-.47 2.08-1.32 2.83-.84.75-1.99 1.17-3.18 1.17H14a2 2 0 0 0-1 3.75A1.3 1.3 0 0 1 12 21" /><path d="M7.5 10.5h.01M12 7.5h.01M16.5 10.5h.01" /></>),
  fraud: svg(<><path d="M12 3a12 12 0 0 0 8.5 3A12 12 0 0 1 12 21 12 12 0 0 1 3.5 6 12 12 0 0 0 12 3" /><path d="M9 12l2 2 4-4" /></>),
  promotions: svg(<><path d="M9 15l6-6" /><path d="M9.5 9.5h.01M14.5 14.5h.01" /><path d="M5 7.2A2.2 2.2 0 0 1 7.2 5h1a2.2 2.2 0 0 0 1.55-.64l.7-.7a2.2 2.2 0 0 1 3.12 0l.7.7c.41.41.97.64 1.55.64h1a2.2 2.2 0 0 1 2.2 2.2v1c0 .58.23 1.14.64 1.55l.7.7a2.2 2.2 0 0 1 0 3.12l-.7.7a2.2 2.2 0 0 0-.64 1.55v1a2.2 2.2 0 0 1-2.2 2.2h-1a2.2 2.2 0 0 0-1.55.64l-.7.7a2.2 2.2 0 0 1-3.12 0l-.7-.7a2.2 2.2 0 0 0-1.55-.64h-1A2.2 2.2 0 0 1 5 16.8v-1a2.2 2.2 0 0 0-.64-1.55l-.7-.7a2.2 2.2 0 0 1 0-3.12l.7-.7A2.2 2.2 0 0 0 5 8.2z" /></>),
  gtm: svg(<><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 6-6" /><path d="M16 8h4v4" /></>),
  import: svg(<><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M7 11l5 5 5-5" /><path d="M12 4v12" /></>),
  mcp: svg(<><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M19 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" /></>),
  n8n: svg(<><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8.5 12h3l4-5M11.5 12l4 5" /></>),
};

export const FEATURES = ["ai", "themes", "fraud", "promotions", "gtm", "import", "mcp", "n8n"];

const INTERVAL_MS = 5000;

export const FeatureSlider = ({ title }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const paused = useRef(false);

  const go = useCallback((to) => setIndex((to + FEATURES.length) % FEATURES.length), []);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const id = setInterval(() => {
      if (!paused.current) setIndex((i) => (i + 1) % FEATURES.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const key = FEATURES[index];

  return (
    <section
      className="sp-fslider"
      aria-roledescription="carousel"
      aria-label={title || t("google.featuresTitle", "What your store can do")}
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onFocus={() => (paused.current = true)}
      onBlur={() => (paused.current = false)}
    >
      <div className="sp-fslider-head">
        <span className="sp-fslider-title">{title || t("google.featuresTitle", "What your store can do")}</span>
        <span className="sp-fslider-count">{index + 1} / {FEATURES.length}</span>
      </div>

      <div className="sp-fslider-slide" key={key} aria-live="polite">
        <span className="sp-fslider-icon">{ICONS[key]}</span>
        <div>
          <strong>{t(`google.features.${key}.title`)}</strong>
          <p>{t(`google.features.${key}.text`)}</p>
        </div>
      </div>

      <div className="sp-fslider-nav">
        <button type="button" className="sp-fslider-arrow" onClick={() => go(index - 1)} aria-label={t("google.prev", "Previous")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
        </button>
        <div className="sp-fslider-dots" role="tablist">
          {FEATURES.map((f, i) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t(`google.features.${f}.title`)}
              className={i === index ? "is-on" : ""}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button type="button" className="sp-fslider-arrow" onClick={() => go(index + 1)} aria-label={t("google.next", "Next")}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </div>

      <ul className="sp-fslider-chips" aria-hidden="true">
        {FEATURES.map((f, i) => (
          <li key={f} className={i === index ? "is-on" : ""} onClick={() => go(i)}>
            <span>{ICONS[f]}</span>
            {t(`google.features.${f}.title`)}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FeatureSlider;
